import { useState, useEffect, useRef } from 'react';
import {
  GameType,
  USERNAME_MAX_LENGTH,
  GAME_METADATA,
  canCreateGameMode,
} from '@swtorbit/shared-socket-events';
import type { GameFaction, FprCompositionRules, GameLocation } from '@swtorbit/shared-socket-events';
import { useGameContext } from '../contexts/GameContext';
import { useAuthContext } from '../contexts/AuthContext';
import { Modal } from '@swtorbit/ui/react';
import { HnsLocationPlanEditor } from './hns/HnsLocationPlanEditor';

interface CreateGameModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Convert GAME_METADATA to array for rendering
const GAME_TYPES = Object.values(GAME_METADATA);

export function CreateGameModal({ isOpen, onClose }: CreateGameModalProps) {
  const { games, createGame } = useGameContext();
  const { permissions } = useAuthContext();

  // Only offer game types this admin is allowed to create. The server gate in
  // GAME_CREATE remains authoritative; this just hides what they can't use.
  // normalizeAdminPermissions guarantees at least one allowed type.
  const allowedTypes = GAME_TYPES.filter((type) => canCreateGameMode(permissions, type.id));

  const [gameType, setGameType] = useState<GameType>(
    (allowedTypes[0]?.id as GameType) ?? GameType.HIDE_AND_SEEK,
  );
  const [gameName, setGameName] = useState('');
  const [debugEnabled, setDebugEnabled] = useState(false);
  const [fprFaction, setFprFaction] = useState<GameFaction | undefined>(undefined);
  const [maxStealthPerTeam, setMaxStealthPerTeam] = useState<number | null>(null);
  const [maxPredationPerTeam, setMaxPredationPerTeam] = useState<number | null>(null);
  const [enforceStandardRoles, setEnforceStandardRoles] = useState(false);
  const [hnsLocationPlan, setHnsLocationPlan] = useState<GameLocation[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const previousGamesCount = useRef(games.length);
  const wasCreatingRef = useRef(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setGameType((allowedTypes[0]?.id as GameType) ?? GameType.HIDE_AND_SEEK);
      setGameName('');
      setDebugEnabled(false);
      setFprFaction(undefined);
      setMaxStealthPerTeam(null);
      setMaxPredationPerTeam(null);
      setEnforceStandardRoles(false);
      setHnsLocationPlan([]);
      setError(null);
      setIsCreating(false);
      wasCreatingRef.current = false;
      previousGamesCount.current = games.length;
    }
    // allowedTypes is derived from permissions; only reset on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Only reset when isOpen changes, not when games.length changes

  // Keep the selected type valid if the allowed set changes (e.g. permissions
  // refresh while the modal is open).
  const allowedIdsKey = allowedTypes.map((type) => type.id).join(',');
  useEffect(() => {
    if (!allowedTypes.some((type) => type.id === gameType)) {
      setGameType((allowedTypes[0]?.id as GameType) ?? GameType.HIDE_AND_SEEK);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedIdsKey, gameType]);

  // Track when we start creating - only capture count when isCreating becomes true
  useEffect(() => {
    if (isCreating && !wasCreatingRef.current) {
      wasCreatingRef.current = true;
      previousGamesCount.current = games.length;
    }
  }, [isCreating, games.length]);

  // Detect when a new game is created - context now auto-selects, so just close modal
  useEffect(() => {
    if (wasCreatingRef.current && games.length > previousGamesCount.current) {
      // A new game was added - context handles selection, just close modal
      setIsCreating(false);
      wasCreatingRef.current = false;
      onClose();
    }
  }, [games.length, onClose]);

  const handleCreateGame = () => {
    setIsCreating(true);
    setError(null);

    try {
      let fprCompositionRules: FprCompositionRules | undefined;
      if (gameType === GameType.FP_RACE && (maxStealthPerTeam !== null || maxPredationPerTeam !== null || enforceStandardRoles)) {
        fprCompositionRules = { maxStealthPerTeam, maxPredationPerTeam, enforceStandardRoles };
      }

      createGame({
        gameType,
        name: gameName.trim() || undefined,
        debugEnabled,
        fprFaction: gameType === GameType.FP_RACE ? fprFaction : undefined,
        fprCompositionRules,
        hnsLocationPlan: (gameType === GameType.HIDE_AND_SEEK || gameType === GameType.HIDE_AND_SEEK_TEAMS) && hnsLocationPlan.length > 0 ? hnsLocationPlan : undefined,
      });
      // Auto-select and close will happen in useEffect when games array updates
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create game';
      setError(message);
      setIsCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal open={isOpen} onClose={onClose} title="Create New Game" footer={
<>
          <button 
            className="btn btn--outline btn--slate" 
            onClick={onClose}
            disabled={isCreating}
          >
            Cancel
          </button>
          <button
            className="btn"
            onClick={handleCreateGame}
            disabled={isCreating}
          >
            {isCreating ? 'Creating...' : 'Create Game'}
          </button>
        </>
}>
          <div className="field">
            <label htmlFor="game-type">Game Type</label>
            <select
              className="select"
              id="game-type"
              value={gameType}
              onChange={(e) => setGameType(e.target.value as GameType)}
              disabled={isCreating}
            >
              {allowedTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.icon} {type.title}
                </option>
              ))}
            </select>
          </div>

          {gameType === GameType.FP_RACE && (
            <>
              <div className="field">
                <label htmlFor="fpr-faction">Faction (optional)</label>
                <select
                  className="select"
                  id="fpr-faction"
                  value={fprFaction ?? ''}
                  onChange={(e) => setFprFaction((e.target.value as GameFaction) || undefined)}
                  disabled={isCreating}
                >
                  <option value="">Any / Both factions</option>
                  <option value="imperial">Imperial</option>
                  <option value="republic">Republic</option>
                </select>
              </div>

              <div className="field">
                <label htmlFor="fpr-stealth-limit">Stealth class limit per team (optional)</label>
                <select
                  className="select"
                  id="fpr-stealth-limit"
                  value={maxStealthPerTeam ?? ''}
                  onChange={(e) => setMaxStealthPerTeam(e.target.value === '' ? null : parseInt(e.target.value, 10))}
                  disabled={isCreating}
                >
                  <option value="">No limit</option>
                  <option value="0">0 — none allowed</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                </select>
              </div>

              <div className="field">
                <label htmlFor="fpr-predation-limit">Predation/Transcendence limit per team (optional)</label>
                <select
                  className="select"
                  id="fpr-predation-limit"
                  value={maxPredationPerTeam ?? ''}
                  onChange={(e) => setMaxPredationPerTeam(e.target.value === '' ? null : parseInt(e.target.value, 10))}
                  disabled={isCreating}
                >
                  <option value="">No limit</option>
                  <option value="0">0 — none allowed</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                </select>
              </div>

              <div className="field checkbox">
                <label>
                  <input
                    className="checkbox"
                    type="checkbox"
                    checked={enforceStandardRoles}
                    onChange={(e) => setEnforceStandardRoles(e.target.checked)}
                    disabled={isCreating}
                  />
                  Require standard roles (1 tank, 1 healer, 2 DPS per team)
                </label>
              </div>
            </>
          )}

          {(gameType === GameType.HIDE_AND_SEEK || gameType === GameType.HIDE_AND_SEEK_TEAMS) && (
            <div className="field">
              <label>Location plan (optional)</label>
              <p className="fs-sm text-slate m-0 mb-2xs">
                Where you plan to play. Players download these maps when they join, so rounds set there show their map straight away.
              </p>
              {isOpen && <HnsLocationPlanEditor value={hnsLocationPlan} onChange={setHnsLocationPlan} disabled={isCreating} />}
            </div>
          )}

          <div className="field">
            <label htmlFor="game-name">Game Name (optional)</label>
            <input
              className="input"
              id="game-name"
              type="text"
              placeholder="e.g., Friday Night Game"
              value={gameName}
              onChange={(e) => setGameName(e.target.value.slice(0, USERNAME_MAX_LENGTH))}
              disabled={isCreating}
            />
          </div>

          <div className="field checkbox">
            <label>
              <input
                className="checkbox"
                type="checkbox"
                checked={debugEnabled}
                onChange={(e) => setDebugEnabled(e.target.checked)}
                disabled={isCreating}
              />
              Enable debug mode on clients
            </label>
          </div>

          {error && (
            <div className="field__error">{error}</div>
          )}
        </Modal>
  );
}
